import React, { useEffect, useState } from 'react';
import {
  FontBoldIcon,
  FontItalicIcon,
  ListBulletIcon,
  Link2Icon,
} from '@radix-ui/react-icons';
import type { Editor } from '@tiptap/react';

interface RichTextEditorToolbarProps {
  editor: Editor | null;
}

export const RichTextEditorToolbar: React.FC<RichTextEditorToolbarProps> = ({
  editor,
}) => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    if (!editor) return undefined;

    const handleUpdate = () => forceUpdate({});

    editor.on('selectionUpdate', handleUpdate);
    editor.on('transaction', handleUpdate);

    return () => {
      editor.off('selectionUpdate', handleUpdate);
      editor.off('transaction', handleUpdate);
    };
  }, [editor]);

  if (!editor) return null;

  const handleLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter URL', previousUrl ?? 'https://');

    if (url === null) return;

    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div className="rich-text-editor__toolbar">
      <button
        type="button"
        className={editor.isActive('bold') ? 'is-active' : ''}
        aria-label="Bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FontBoldIcon />
      </button>
      <button
        type="button"
        className={editor.isActive('italic') ? 'is-active' : ''}
        aria-label="Italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FontItalicIcon />
      </button>
      <button
        type="button"
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        aria-label="Bulleted list"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListBulletIcon />
      </button>
      <button
        type="button"
        className={editor.isActive('link') ? 'is-active' : ''}
        aria-label="Link"
        onClick={handleLink}
      >
        <Link2Icon />
      </button>
    </div>
  );
};
